/**
 * iter351 — Fil de discussion interne d'un OR, partagé administration ↔ atelier.
 * Texte, note vocale, photo/vidéo. Jamais visible par le client.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Mic, Square, Camera, Send, Loader2, FileText, MessageSquare, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_BADGE = {
  demande: { label: "Demande · en attente de réponse", cls: "bg-orange-50 text-orange-800 border-orange-300" },
  reponse: { label: "Réponse admin", cls: "bg-[#52a675]/10 text-[#2d5f3f] border-[#52a675]/30" },
  systeme: { label: "Journal", cls: "bg-gray-100 text-gray-600 border-gray-200" },
};

const heure = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export const OrFil = ({ orId, compact = false, onChanged, peutDemander = false }) => {
  const [items, setItems] = useState([]);
  const [moi, setMoi] = useState(null);
  const [texte, setTexte] = useState("");
  const [demande, setDemande] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/or/${orId}/fil`);
    setItems(data.items || []);
    setMoi(data.moi);
    onChanged?.();
  }, [orId, onChanged]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "nearest" }); }, [items.length]);

  const envoyer = async () => {
    if (!texte.trim()) return;
    setBusy(true);
    try {
      await api.post(`/or/${orId}/fil`, { texte, demande });
      if (demande) toast.success("Demande transmise — le dossier passe en attente de réponse");
      setTexte("");
      setDemande(false);
      await load();
    } catch {
      toast.error("Message non envoyé");
    } finally {
      setBusy(false);
    }
  };

  const envoyerFichier = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("fichier", file);
    setBusy(true);
    try {
      await api.post(`/or/${orId}/fil/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  const toggleVocal = async () => {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: mime });
        const ext = mime.includes("webm") ? "webm" : "m4a";
        await envoyerFichier(new File([blob], `note-vocale.${ext}`, { type: mime }));
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Micro indisponible sur cet appareil");
    }
  };

  const joindreAuDossier = async (messageId) => {
    try {
      const { data } = await api.post(`/or/${orId}/fil/${messageId}/vers-photos`);
      toast.success(data.deja ? "Déjà dans les annexes du dossier" : "Photo ajoutée aux annexes du dossier");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ajout impossible");
    }
  };

  const bulle = (m) => {
    const mien = m.auteur_id === moi;
    const badge = TYPE_BADGE[m.type];
    const url = m.media ? `${API}/api/or/fil/media/${m.media.gridfs_id}` : null;
    return (
      <div key={m.id} className={`flex ${mien ? "justify-end" : "justify-start"}`} data-testid={`fil-msg-${m.id}`}>
        <div
          className={`max-w-[85%] rounded-2xl px-3 py-2 border ${
            mien ? "bg-[#52a675]/10 border-[#52a675]/30" : "bg-white border-gray-200"
          }`}
        >
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[11px] font-semibold text-gray-700">{m.auteur_nom}</span>
            <span className="text-[10px] text-gray-400">{heure(m.created_at)}</span>
            {badge && <Badge variant="outline" className={`text-[9px] py-0 ${badge.cls}`}>{badge.label}</Badge>}
          </div>
          {m.texte && <div className="text-sm whitespace-pre-wrap text-gray-900">{m.texte}</div>}
          {url && m.type === "photo" && (
            <div className="mt-1">
              <a href={url} target="_blank" rel="noreferrer">
                <img src={url} alt={m.media.nom} className="rounded-lg max-h-56 object-cover" data-testid={`fil-photo-${m.id}`} />
              </a>
              {m.joint_or ? (
                <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-[#2d5f3f]" data-testid={`fil-photo-jointe-${m.id}`}>
                  <ImagePlus className="w-3 h-3" /> Dans les annexes du dossier
                </span>
              ) : (
                <button
                  onClick={() => joindreAuDossier(m.id)}
                  className="mt-1 inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-[#2d5f3f] underline"
                  data-testid={`fil-photo-joindre-${m.id}`}
                >
                  <ImagePlus className="w-3 h-3" /> Ajouter aux annexes du dossier
                </button>
              )}
            </div>
          )}
          {url && m.type === "vocal" && (
            <audio controls src={url} className="mt-1 w-56 max-w-full" data-testid={`fil-audio-${m.id}`} />
          )}
          {url && m.type === "video" && (
            <video controls src={url} className="mt-1 rounded-lg max-h-56" data-testid={`fil-video-${m.id}`} />
          )}
          {url && m.type === "fichier" && (
            <a href={url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 text-xs text-[#2d5f3f] underline">
              <FileText className="w-3.5 h-3.5" /> {m.media.nom}
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="or-fil">
      <div
        className={`space-y-2 overflow-y-auto ${compact ? "max-h-72" : "max-h-[26rem]"} pr-1`}
        data-testid="fil-messages"
      >
        {items.length === 0 && (
          <div className="text-center text-xs text-gray-400 py-6 flex flex-col items-center gap-1" data-testid="fil-vide">
            <MessageSquare className="w-5 h-5 text-gray-300" />
            Aucun échange sur ce dossier
          </div>
        )}
        {items.map(bulle)}
        <div ref={bottomRef} />
      </div>

      <div className="flex items-end gap-1.5 mt-2">
        <Textarea
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          placeholder={demande ? "Ta demande à l'administration…" : "Écrire à l'équipe…"}
          rows={1}
          className={`resize-none min-h-[44px] max-h-28 ${demande ? "border-orange-300 bg-orange-50/40" : ""}`}
          data-testid="fil-input"
        />
        <input
          ref={fileRef} type="file" accept="image/*,video/*,application/pdf" className="hidden"
          onChange={(e) => { envoyerFichier(e.target.files?.[0]); e.target.value = ""; }}
          data-testid="fil-file-input"
        />
        <Button
          variant="outline" size="icon" className="h-11 w-11 flex-shrink-0 border-gray-300"
          onClick={() => fileRef.current?.click()} disabled={busy}
          data-testid="fil-photo-btn"
        >
          <Camera className="w-4 h-4" />
        </Button>
        <Button
          variant="outline" size="icon"
          className={`h-11 w-11 flex-shrink-0 ${recording ? "border-red-400 text-red-600 animate-pulse" : "border-gray-300"}`}
          onClick={toggleVocal} disabled={busy}
          data-testid="fil-vocal-btn"
        >
          {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </Button>
        <Button
          size="icon" className="h-11 w-11 flex-shrink-0 bg-[#52a675] hover:bg-[#2d5f3f]"
          onClick={envoyer} disabled={busy || !texte.trim()}
          data-testid="fil-envoyer-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>

      {peutDemander && (
        <button
          onClick={() => setDemande(!demande)}
          className={`mt-2 w-full py-2 rounded-xl border-2 text-xs font-semibold transition-colors ${
            demande ? "border-orange-400 bg-orange-50 text-orange-800" : "border-gray-200 text-gray-500"
          }`}
          data-testid="fil-demande-toggle"
        >
          {demande
            ? "✓ Ce message est une demande — le dossier passera en attente de réponse"
            : "Marquer ce message comme une demande (devis, pièces, validation…)"}
        </button>
      )}
    </div>
  );
};

export default OrFil;
